import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  EmojiEvents as EmojiEventsIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import moment from 'moment';
import MobileHeader from '../components/MobileHeader';

const OnlineExamScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [startExamLoading, setStartExamLoading] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        'api/school-common/online-exam-master/get-scheduled-online-exam-list-student',
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading exams:', error);
      handleCatch(error);
      setItems([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const startExam = async (id) => {
    try {
      setStartExamLoading(id);
      const token = localStorage.getItem('userToken');
      const response = await api.post(
        `api/school-common/online-exam/start-exam?examId=${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      localStorage.setItem('currentExam', JSON.stringify(response.data));
      navigate('/online-exam-start');
    } catch (error) {
      console.error('Error starting exam:', error);
      setNotification({ open: true, message: 'Failed to start exam. Please try again.', severity: 'error' });
    } finally {
      setStartExamLoading(null);
    }
  };

  const onRefresh = async () => {
    await loadData();
  };

  const getStatusColor = (status) => {
    if (!status) return '#999';
    const s = status.toLowerCase();
    if (s === 'active' || s === 'scheduled') return colors.BLUE;
    if (s === 'completed' || s === 'finished') return '#4CAF50';
    if (s === 'expired') return '#f44336';
    return '#FF9800';
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Online Exams"
        showBack={true}
        rightAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/online-exam-result')}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                fontSize: '0.72rem',
                px: 1.5,
                py: 0.5,
                borderRadius: 20,
                textTransform: 'none',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              View Results
            </Button>
            <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>Loading exams...</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}>📝</Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Exams Scheduled
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              No online exams are scheduled for you right now.
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : (
          items.map((item, index) => {
            const isStarting = startExamLoading === item.id;
            const statusColor = getStatusColor(item.examStatus);
            return (
              <Box
                key={index}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  mx: 0.5,
                  mt: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Header row */}
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: 15, color: '#222', lineHeight: 1.3 }}>
                        {item.examName}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: colors.BLUE, fontWeight: '500', mt: 0.3 }}>
                        📚 {item.subjectName}
                      </Typography>
                    </Box>
                    {item.examStatus && (
                      <Chip
                        label={item.examStatus}
                        size="small"
                        sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 'bold', fontSize: 11 }}
                      />
                    )}
                  </Box>

                  {/* Stats row */}
                  <Box
                    sx={{
                      display: 'flex',
                      mt: 1.5,
                      mb: 1,
                      bgcolor: '#F5F7FA',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ flex: 1, py: 1, px: 1, textAlign: 'center', borderRight: '1px solid #E0E4EA' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                        <TimerIcon sx={{ fontSize: 14, color: colors.BLUE }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                          {item.duration} min
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: '#888', mt: 0.2 }}>Duration</Typography>
                    </Box>
                    <Box sx={{ flex: 1, py: 1, px: 1, textAlign: 'center', borderRight: '1px solid #E0E4EA' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                        <EmojiEventsIcon sx={{ fontSize: 14, color: '#FF9800' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                          {item.totalMarks}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: '#888', mt: 0.2 }}>Total</Typography>
                    </Box>
                    <Box sx={{ flex: 1, py: 1, px: 1, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                          {item.passMarks}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: '#888', mt: 0.2 }}>Pass</Typography>
                    </Box>
                  </Box>

                  {/* Dates */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 13, color: '#888' }} />
                    <Typography sx={{ fontSize: 12, color: '#666' }}>
                      {moment(item.startDate).format('DD MMM, hh:mm A')}
                      {item.endDate && ` – ${moment(item.endDate).format('DD MMM, hh:mm A')}`}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Start button */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={isStarting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => startExam(item.id)}
                    disabled={isStarting}
                    sx={{
                      bgcolor: colors.BLUE,
                      height: 44,
                      borderRadius: 22,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: 14,
                      '&:hover': { bgcolor: '#0052A3' },
                    }}
                  >
                    {isStarting ? 'Starting...' : 'Start Exam'}
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnlineExamScreen;
